import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api.js'
import { useAuth } from '../hooks/useAuth.js'
import { count as fmtCount, humanise, money, percent } from '../lib/format.js'
import { Card, Empty, PageHead, Segmented, Stat, StatsSkeleton, Tabs } from '../components/ui.js'
import { BarChart, Donut, Funnel, LineChart, compact } from '../components/charts.js'
import { IconByName } from '../components/iconByName.js'
import { ArrowRightIcon, BagIcon, CartIcon, DownloadIcon, StoreIcon, TargetIcon } from '../components/icons.js'

type View = 'overview' | 'orders' | 'revenue' | 'customers' | 'conversion' | 'payments' | 'abandoned'

const VIEWS: Array<{ key: View; label: string; path: string }> = [
  { key: 'overview', label: 'Overview', path: '/analytics' },
  { key: 'orders', label: 'Orders', path: '/analytics/orders' },
  { key: 'revenue', label: 'Revenue', path: '/analytics/revenue' },
  { key: 'customers', label: 'Customers', path: '/analytics/customers' },
  { key: 'conversion', label: 'Conversion', path: '/analytics/conversion' },
  { key: 'payments', label: 'Payments', path: '/analytics/payments' },
  { key: 'abandoned', label: 'Abandoned Checkouts', path: '/analytics/abandoned' },
]

interface Kpi {
  label: string
  value: string
  icon: string
  tone: string
  delta: number | null
  upIsGood: boolean
}

/** What `/v1/analytics` answers with. */
interface AnalyticsPayload {
  range: string
  rangeLabel: string
  comparedTo: string
  ranges: Record<string, { label: string; days: number; bucket: string }>
  kpis: Kpi[]
  series: { labels: string[]; revenue: number[]; orders: number[]; maxRevenue: number; maxOrders: number }
  previousRevenue: number[]
  funnel: Array<{ label: string; count: number; pct: number }>
  paymentMethods: { slices: Array<{ key: string; label: string; count: number; pct: number; color: string }>; total: number }
  ordersByStatus: Array<{ status: string; count: number }>
  sessions: number[]
  topProducts: Array<{ title: string; units: number; revenue: string }>
}

const STATUS_COLOR: Record<string, string> = {
  paid: 'var(--ok)',
  cod_pending: 'var(--warn)',
  pending: 'var(--info)',
  processing: 'var(--info)',
  failed: 'var(--bad)',
  refunded: 'var(--accent)',
}

/**
 * Seven routes, one component, one endpoint.
 *
 * The headline figures, funnel and payment split come from the same engine the
 * Dashboard uses, so the two screens cannot quote different revenue for the same
 * week. What is *not* here: a visitors line. Nothing counts visitors — the
 * checkout only loads once a shopper presses Buy Now — so the traffic chart
 * plots checkouts opened, which is the real number, labelled as such.
 */
export function AnalyticsPage(): React.ReactNode {
  const { view: viewParam } = useParams<{ view?: View }>()
  const { shop } = useAuth()
  const navigate = useNavigate()
  const [range, setRange] = useState('7d')

  const view: View = VIEWS.some((entry) => entry.key === viewParam) ? (viewParam as View) : 'overview'

  const { data, isPending, error } = useQuery({
    queryKey: ['analytics', shop?.id, range],
    queryFn: () => api<AnalyticsPayload>('/v1/analytics', { query: { range } }),
    enabled: Boolean(shop?.id),
  })

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          Analytics are per store. Choose one from the switcher above.
        </Empty>
      </Card>
    )
  }

  const ranges = data ? Object.entries(data.ranges).map(([key, meta]) => ({ key, label: meta.label })) : []

  return (
    <>
      <PageHead
        title="Analytics"
        subtitle={data ? `${data.rangeLabel} · ${data.comparedTo}` : 'Track performance and growth across your store.'}
        actions={
          <>
            {ranges.length > 0 && <Segmented label="Date range" value={range} onChange={setRange} options={ranges} />}
            <button type="button" className="btn btn--primary" onClick={() => window.print()}>
              <DownloadIcon size={16} />
              Export report
            </button>
          </>
        }
      />

      <Card flush>
        <Tabs
          value={view}
          onChange={(key) => navigate(VIEWS.find((entry) => entry.key === key)!.path)}
          tabs={VIEWS.map((entry) => ({ key: entry.key, label: entry.label }))}
        />
      </Card>

      {error && (
        <Card>
          <Empty error title="Could not load analytics">
            {(error as Error).message}
          </Empty>
        </Card>
      )}

      {isPending && <StatsSkeleton count={5} />}

      {data && (
        <>
          <section className="grid grid--4">
            {data.kpis.map((kpi) => (
              <Stat
                key={kpi.label}
                label={kpi.label}
                value={kpi.value}
                tone={kpi.tone}
                delta={kpi.delta}
                upIsGood={kpi.upIsGood}
                icon={<IconByName name={kpi.icon} />}
                footnote={<span className="faint">{data.comparedTo}</span>}
              />
            ))}
          </section>

          {(view === 'overview' || view === 'revenue') && (
            <Card title="Revenue" subtitle="This window against the one before it">
              <LineChart
                labels={data.series.labels}
                series={[
                  { key: 'revenue', label: 'Revenue (₹)', color: 'var(--brand)', values: data.series.revenue },
                  {
                    key: 'previous',
                    label: 'Previous period (₹)',
                    color: 'var(--ink-faint)',
                    values: data.previousRevenue,
                    dashed: true,
                  },
                ]}
                formatValue={(value) => `₹${compact(value)}`}
              />
            </Card>
          )}

          {(view === 'overview' || view === 'orders') && (
            <div className="grid grid--2">
              <Card title="Orders by status">
                {data.ordersByStatus.length === 0 ? (
                  <Empty icon={<BagIcon size={22} />} title="No orders in this window" />
                ) : (
                  <Donut
                    centreValue={fmtCount(data.ordersByStatus.reduce((sum, row) => sum + row.count, 0))}
                    centreLabel="orders"
                    slices={data.ordersByStatus.map((row) => ({
                      key: row.status,
                      label: humanise(row.status),
                      value: row.count,
                      color: STATUS_COLOR[row.status] ?? 'var(--ink-faint)',
                    }))}
                  />
                )}
              </Card>

              {/* "Checkouts opened", never "visitors" — see the component note. */}
              <Card title="Checkouts opened vs orders" subtitle="How many started, how many finished">
                <BarChart
                  labels={data.series.labels}
                  bars={{ key: 'sessions', label: 'Checkouts opened', color: 'var(--brand)', values: data.sessions }}
                  line={{ key: 'orders', label: 'Orders placed', color: 'var(--ok)', values: data.series.orders }}
                />
              </Card>
            </div>
          )}

          {(view === 'overview' || view === 'payments') && (
            <div className="grid grid--2">
              <Card title="Revenue by payment method">
                {data.paymentMethods.total === 0 ? (
                  <Empty icon={<BagIcon size={22} />} title="No settled orders in this window" />
                ) : (
                  <Donut
                    centreValue={fmtCount(data.paymentMethods.total)}
                    centreLabel="settled orders"
                    slices={data.paymentMethods.slices.map((slice) => ({
                      key: slice.key,
                      label: slice.label,
                      value: slice.count,
                      color: slice.color,
                    }))}
                  />
                )}
              </Card>

              <Card title="Top products" subtitle="By settled revenue" flush>
                {data.topProducts.length === 0 ? (
                  <Empty icon={<BagIcon size={22} />} title="Nothing sold in this window">
                    Products are ranked from what each order recorded at the time it was placed.
                  </Empty>
                ) : (
                  <ul className="list">
                    {data.topProducts.map((product) => (
                      <li key={product.title}>
                        <span className="list__text">
                          <strong>{product.title}</strong>
                          <small>{product.units} sold</small>
                        </span>
                        <span className="list__value">{money(product.revenue)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          )}

          {(view === 'overview' || view === 'conversion') && (
            <div className="grid grid--main">
              <Card title="Checkout funnel" subtitle="Where shoppers drop off between opening the checkout and paying">
                {data.funnel.length === 0 ? (
                  <Empty icon={<TargetIcon size={22} />} title="No checkout sessions in this window" />
                ) : (
                  <Funnel stages={data.funnel} />
                )}
              </Card>

              <Card title="What the funnel says" accent>
                {data.funnel.length > 1 ? (
                  <ul className="list list--plain">
                    {data.funnel.slice(1).map((stage, index) => {
                      const previous = data.funnel[index]!
                      const lost = previous.count - stage.count
                      return (
                        <li key={stage.label} style={{ paddingLeft: 0, alignItems: 'flex-start' }}>
                          <span className="list__text">
                            <strong>
                              {lost.toLocaleString('en-IN')} lost before {stage.label.toLowerCase()}
                            </strong>
                            <small>
                              {previous.count.toLocaleString('en-IN')} reached {previous.label.toLowerCase()},{' '}
                              {stage.count.toLocaleString('en-IN')} went on
                            </small>
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                ) : (
                  <p className="muted" style={{ fontSize: '.87rem' }}>
                    Not enough checkout sessions in this window to show where shoppers drop.
                  </p>
                )}

                <Link to="/engagement/abandoned" className="btn btn--primary btn--block" style={{ marginTop: 14 }}>
                  <CartIcon size={16} />
                  Recover abandoned checkouts
                  <ArrowRightIcon size={16} />
                </Link>
              </Card>
            </div>
          )}

          {(view === 'customers' || view === 'abandoned') && (
            <Card title={view === 'customers' ? 'Orders over time' : 'Checkouts opened vs orders'}>
              <BarChart
                labels={data.series.labels}
                bars={{ key: 'sessions', label: 'Checkouts opened', color: 'var(--brand)', values: data.sessions }}
                line={{ key: 'orders', label: 'Orders placed', color: 'var(--ok)', values: data.series.orders }}
              />
              <p className="muted" style={{ fontSize: '.82rem', marginTop: 12 }}>
                {view === 'customers'
                  ? 'Order volume over the window. Cohorts are grouped by order rather than by customer, so a repeat buyer appears in each period they bought in.'
                  : 'The abandoned list itself, with values and recovery, is under Engagement → Abandoned checkouts.'}
              </p>
            </Card>
          )}

          {view === 'orders' && (
            <Card title="Orders per bucket">
              <LineChart
                labels={data.series.labels}
                series={[{ key: 'orders', label: 'Orders', color: 'var(--ok)', values: data.series.orders }]}
                formatValue={(value) => compact(value)}
              />
            </Card>
          )}

          {view === 'revenue' && data.paymentMethods.total > 0 && (
            <Card title="Settled orders by method" flush>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Method</th>
                      <th className="right">Orders</th>
                      <th className="right">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.paymentMethods.slices.map((slice) => (
                      <tr key={slice.key}>
                        <td>
                          <span className="table__cell">
                            <span className="chart__swatch" style={{ background: slice.color, borderRadius: '50%' }} />
                            {slice.label}
                          </span>
                        </td>
                        <td className="right num">{fmtCount(slice.count)}</td>
                        <td className="right num">{percent(slice.pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </>
  )
}
